/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { RadioButton, RadioButtonGroup, Callout } from "@octopusdeploy/design-system-components";
import { TextWithLabel as Text, StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { TagSetResource, TagResource, MultiTenancyStatusResource, TagSetType, TagSetScope } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { sortBy } from "lodash";
import * as React from "react";
import { useDispatch } from "react-redux";
import { repository } from "~/clientInstance";
import Chip from "~/components/Chips/Chip";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { OptionalFormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import Markdown from "~/components/Markdown";
import { SupportedTagSetScopes, TagSetScopeMultiSelect } from "~/components/MultiSelect/TagSetScopeMultiSelect";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import Tag from "~/components/Tag";
import { ExpandableFormSection, Summary } from "~/components/form";
import MarkdownEditor from "~/components/form/MarkdownEditor/MarkdownEditor";
import { required } from "~/components/form/Validators";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import InternalRedirect from "../../../../components/Navigation/InternalRedirect/InternalRedirect";
import { configurationActions } from "../../../configuration/reducers/configurationArea";
import TagListEdit from "./TagListEdit/TagListEdit";
import type TagModel from "./TagListEdit/TagModel";
import styles from "./style.module.less";
interface TagSetEditModel {
    name: string;
    description: string;
    tags: TagModel[];
    type?: TagSetType;
    scope?: TagSetScope[];
}
interface CreateTenantTagSetProps {
    create: true;
}
interface SpecificTagSetPageProps {
    tagSetId: string;
}
type TagSetPageProps = (CreateTenantTagSetProps | SpecificTagSetPageProps) & {
    spaceId: string;
};
type TagSetPageInternalProps = TagSetPageProps & {
    onSpaceMultiTenancyStatusFetched: (status: MultiTenancyStatusResource) => void;
};
interface TagSetEditState extends OptionalFormBaseComponentState<TagSetEditModel> {
    tagSet: TagSetResource;
    deleted: boolean;
    newId?: string;
    currentTag: Partial<TagModel>;
}
class TagSetPageInternal extends OptionalFormBaseComponent<TagSetPageInternalProps, TagSetEditState, TagSetEditModel> {
    private tagListEdit: TagListEdit | null = undefined!;
    private isExtendedTagSetsEnabled = isFeatureToggleEnabled("extended-tag-sets");
    constructor(props: TagSetPageInternalProps) {
        super(props);
        this.state = {
            tagSet: null!,
            model: null!,
            cleanModel: null!,
            deleted: false,
            currentTag: null!,
        };
    }
    async componentDidMount() {
        if (this.state.deleted) {
            return;
        }
        await this.doBusyTask(async () => {
            const tagSet = isSpecificTagSetPageProps(this.props) ? await repository.TagSets.get(this.props.tagSetId) : null!;
            this.setState({ tagSet, model: this.buildModel(tagSet!), cleanModel: this.buildModel(tagSet!) });
        }, { timeOperationOptions: timeOperationOptions.forInitialLoad() });
    }
    buildModel(tagSet: TagSetResource): TagSetEditModel {
        if (tagSet) {
            return {
                name: tagSet.Name,
                description: tagSet.Description,
                type: tagSet.Type,
                scope: tagSet.Scopes && tagSet.Scopes.length > 0 ? tagSet.Scopes : [],
                tags: tagSet.Tags.map((t: TagResource) => ({
                    originalId: t.Id,
                    localId: t.Id || Math.random() + "",
                    name: t.Name,
                    description: t.Description,
                    sortOrder: t.SortOrder,
                    color: t.Color,
                    deleted: false,
                })),
            };
        }
        return {
            name: "",
            description: "",
            tags: [],
        };
    }
    handleDeleteConfirm = async () => {
        const result = await repository.TagSets.del(this.state.tagSet);
        const status = await repository.Tenants.status();
        this.props.onSpaceMultiTenancyStatusFetched(status);
        this.setState(() => {
            return {
                model: null,
                cleanModel: null, //reset model so that dirty state doesn't prevent navigation
                deleted: true,
            };
        });
        return true;
    };
    handleSaveClick = async () => {
        await this.doBusyTask(async () => {
            //the child component actually has a more up to date view of the world than
            //we do, especially when a user has partially added an item and forgotten
            //to hit save. This is a... _less than ideal_ way around the problem.
            //Thes components could do with a refactor
            const tags = this.tagListEdit ? this.tagListEdit.getTagSetIncludingAnyNewTag() : this.state.model!.tags;
            let tagSet: TagSetResource = {
                ...this.state.tagSet,
                Name: this.state.model!.name,
                Description: this.state.model!.description,
                Type: this.state.model!.type,
                Scopes: this.state.model!.scope,
                Tags: tags
                    .filter((t) => !t.deleted)
                    .map((tag) => ({
                    Id: tag.originalId,
                    Name: tag.name,
                    SortOrder: tag.sortOrder,
                    Color: tag.color,
                    Description: tag.description,
                    CanonicalTagName: null!,
                })),
            };
            if (tagSet.Type && tagSet.Type === "FreeText") {
                tagSet.Tags = [];
            }
            tagSet = await repository.TagSets.save(tagSet);
            this.setState({
                tagSet,
                model: this.buildModel(tagSet),
                cleanModel: this.buildModel(tagSet),
                newId: isSpecificTagSetPageProps(this.props) ? null! : tagSet.Id,
            });
        });
    };
    descriptionSummary() {
        return this.state.model!.description ? Summary.summary(<Markdown markup={this.state.model!.description}/>) : Summary.placeholder("No description provided");
    }
    nameSummary() {
        return this.state.model!.name ? Summary.summary(this.state.model!.name) : Summary.placeholder("Please enter a name for your tag set");
    }
    tagsSummary() {
        return this.state.model!.tags && this.state.model!.tags.length > 0
            ? Summary.summary(<div>
                      {sortBy(this.state.model!.tags, (tag) => tag.sortOrder)
                    .map((tag) => this.tagModelToResource(tag))
                    .map((tag, i) => (<Tag tagName={tag.Name} description={tag.Description} tagColor={tag.Color} key={i}/>))}
                  </div>)
            : Summary.placeholder("No tags have been provided");
    }
    tagModelToResource(tag: TagModel): TagResource {
        return {
            Id: tag.originalId,
            Name: tag.name,
            SortOrder: tag.sortOrder,
            Color: tag.color,
            Description: tag.description,
            CanonicalTagName: null!,
        };
    }
    scopeSummary() {
        return this.state.model!.scope && this.state.model!.scope.length > 0
            ? Summary.summary(<div>
                      {this.state.model!.scope.map((scope, i) => (<Chip key={i} description={`${scope}: ${scope}`}>
                              {scope}
                          </Chip>))}
                  </div>)
            : Summary.placeholder("No scope");
    }
    typeSummary() {
        const type = this.state.model!.type || "Not Set";
        return Summary.summary(type);
    }
    getTagSetScopeItems(): {
        Id: string;
        Name: string;
    }[] {
        return SupportedTagSetScopes.map((scope) => ({ Id: scope, Name: scope }));
    }
    shouldDisableFormSaveButton() {
        return !this.state.model?.name || (this.isExtendedTagSetsEnabled && (!this.state.model?.type || !this.state.model?.scope?.length));
    }
    render() {
        const isSpecificTagSet = isSpecificTagSetPageProps(this.props);
        const newTitle = this.isExtendedTagSetsEnabled ? "Create Tag Set" : "Create Tenant Tag Set";
        const title = !isSpecificTagSet ? newTitle : this.state.model ? this.state.model.name : StringHelper.ellipsis;
        const legacyOverflowActions = isSpecificTagSetPageProps(this.props)
            ? [
                OverflowMenuItems.deleteItemDefault("tag set", this.handleDeleteConfirm, { permission: Permission.TagSetDelete }, undefined, <Callout title={"In-use tenant tag sets must not be deleted"} type={"warning"}>
                          `If you are using Config as Code, please check the 'tenant_tags' fields of your deployment and runbook OCL files for references to this tag set before deleting.`
                      </Callout>),
                [
                    OverflowMenuItems.navItem("Audit Trail", links.auditPage.generateUrl({ regardingAny: [this.props.tagSetId] }), {
                        permission: Permission.EventView,
                        wildcard: true,
                    }),
                ],
            ]
            : [];
        const overflowActions = OverflowMenuConverterVNext.convertAll(legacyOverflowActions);
        const shouldShowTagsSection = !this.isExtendedTagSetsEnabled || (this.isExtendedTagSetsEnabled && this.state.model?.type !== "FreeText");
        return (<LegacyForm saveText="Tag Set details changed" model={this.state.model} cleanModel={this.state.cleanModel} savePermission={{ permission: !isSpecificTagSet ? Permission.TagSetCreate : Permission.TagSetEdit }} onSaveClick={this.handleSaveClick} forceDisableFormSaveButton={this.shouldDisableFormSaveButton()}>
                {({ FormContent, createSaveAction }) => (<PageContent header={{
                    primaryAction: createSaveAction({}),
                    title,
                    breadcrumbs: [
                        {
                            label: this.isExtendedTagSetsEnabled ? "Tag Sets" : "Tenant Tag Sets",
                            pageUrl: links.tagSetsPage.generateUrl({ spaceId: this.props.spaceId }),
                        },
                    ],
                    overflowActions: overflowActions.menuItems,
                }} busy={this.state.busy} errors={this.errors}>
                        {overflowActions.dialogs}
                        <FormContent expandAllOnMount={!isSpecificTagSet}>
                            {this.state.deleted && <InternalRedirect to={links.tagSetsPage.generateUrl({ spaceId: this.props.spaceId })}/>}
                            {this.state.newId && <InternalRedirect to={links.tagSetPage.generateUrl({ spaceId: this.props.spaceId, tagSetId: this.state.newId })}/>}
                            {this.state.deleted && <InternalRedirect to={links.tagSetsPage.generateUrl({ spaceId: this.props.spaceId })}/>}
                            {this.state.newId && <InternalRedirect to={links.tagSetPage.generateUrl({ spaceId: this.props.spaceId, tagSetId: this.state.newId })}/>}
                            {this.state.model && (<div className={styles.expanderContainer}>
                                    <ExpandableFormSection errorKey="name" title="Name" focusOnExpandAll summary={this.nameSummary()} help={"A short, memorable, unique name for this tenant tag set. Examples: Customer Type, Hosting, Module."}>
                                        <Text value={this.state.model.name} onChange={(name) => this.setModelState({ name })} label={"Tag Set name"} validate={required("Please enter a Tag Set name")} autoFocus={true}/>
                                    </ExpandableFormSection>

                                    <ExpandableFormSection errorKey="description" title="Description" summary={this.descriptionSummary()} help={"This summary will be presented to users when tagging the tenant, or applying tenant tags to other entities."}>
                                        <MarkdownEditor value={this.state.model.description} label={"Tag Set description"} onChange={(description) => this.setModelState({ description })}/>
                                    </ExpandableFormSection>
                                    {this.isExtendedTagSetsEnabled && (<>
                                            <ExpandableFormSection errorKey="scope" title="Scope" summary={this.scopeSummary()} help="Select the scope/s of the tag sets">
                                                <div>
                                                    <TagSetScopeMultiSelect value={this.state.model.scope || []} label="Tag sets scope" items={this.getTagSetScopeItems()} onChange={(scope) => this.setModelState({ scope: scope as TagSetScope[] })}/>
                                                </div>
                                            </ExpandableFormSection>

                                            <ExpandableFormSection errorKey="type" title="Type" summary={this.typeSummary()} help="Select the type of the tag sets">
                                                <RadioButtonGroup value={this.state.model.type || ""} onChange={(type) => this.setModelState({ type: type as TagSetType })}>
                                                    <RadioButton value="SingleSelect" label="Single Select"/>
                                                    <RadioButton value="MultiSelect" label="Multi Select"/>
                                                    <RadioButton value="FreeText" label="Free Text"/>
                                                </RadioButtonGroup>
                                            </ExpandableFormSection>
                                        </>)}

                                    {shouldShowTagsSection && (<ExpandableFormSection errorKey="tags" title={this.isExtendedTagSetsEnabled ? "Tags" : "Tenant Tags"} summary={this.tagsSummary()} help={!!this.state.model.tags.find((t) => t.deleted && !!t.originalId) ? (<Callout type={"warning"} title={"In-use tenant tags must not be deleted"}>
                                                        {`This action will fail if the tenant tag is in use in a non Config as Code project.`}
                                                        <br /> <br />
                                                        {`If you are using Config as Code, please check the 'tenant_tags' fields of your deployment and runbook OCL files to ensure this tag is not in use.`}
                                                    </Callout>) : (<div>&nbsp;</div>)}>
                                            <div>
                                                <TagListEdit tags={this.state.model.tags} currentTag={this.state.currentTag} onChange={(tags) => {
                            this.setModelState({ tags: [...tags] });
                        }} onClose={(x) => {
                            this.setState({ currentTag: x });
                        }} ref={(tagListEdit) => (this.tagListEdit = tagListEdit)}/>
                                            </div>
                                        </ExpandableFormSection>)}
                                </div>)}
                        </FormContent>
                    </PageContent>)}
            </LegacyForm>);
    }
}
function isSpecificTagSetPageProps(props: CreateTenantTagSetProps | SpecificTagSetPageProps): props is SpecificTagSetPageProps {
    return "tagSetId" in props;
}
export function TagSetPage(props: TagSetPageProps) {
    const dispatch = useDispatch();
    const onSpaceMultiTenancyStatusFetched = React.useCallback((status: MultiTenancyStatusResource) => dispatch(configurationActions.spaceMultiTenancyStatusFetched(status)), [dispatch]);
    return <TagSetPageInternal onSpaceMultiTenancyStatusFetched={onSpaceMultiTenancyStatusFetched} {...props}/>;
}
